"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Accordion = Accordion;
const jsx_runtime_1 = require("@emotion/react/jsx-runtime");
const utils_1 = require("@react-aria/utils");
const react_1 = require("react");
const react_aria_1 = require("react-aria");
const Icon_1 = require("./Icon");
const Css_1 = require("../Css");
const utils_2 = require("../utils");
function Accordion(props) {
    const { title, children, size, disabled = false, defaultExpanded = false, compact = false, topBorder = compact ? false : true, bottomBorder = false, index, setExpandedIndex, titleOnClick, omitPadding = false, xss, } = props;
    const tid = (0, utils_2.useTestIds)(props, "accordion");
    const id = (0, utils_1.useId)();
    const [expanded, setExpanded] = (0, react_1.useState)(defaultExpanded && !disabled);
    const { isFocusVisible, focusProps } = (0, react_aria_1.useFocusRing)();
    const contentRef = (0, react_1.useRef)(null);
    // On initial render, if the accordion is expanded, then set `height` to auto to avoid unnecessary animation on render.
    const [contentHeight, setContentHeight] = (0, react_1.useState)(expanded ? "auto" : "0");
    (0, react_1.useEffect)(() => {
        setExpanded(defaultExpanded && !disabled);
    }, [defaultExpanded, disabled]);
    (0, react_1.useEffect)(() => {
        // When the `expanded` value changes - If true, it means the Accordion's content has been rendered, Otherwise, it's been hidden
        // Then when the content is displayed, the calculate its height so we can give this value to the container to animate height smoothly.
        // When content is removed, simply set the height back to 0
        setContentHeight(expanded && contentRef.current ? `${contentRef.current.scrollHeight}px` : "0");
    }, [expanded]);
    // Using a resizing observer to check if the content of the accordion changes (i.e. lazy loaded image, auto-sizing textarea, etc..),
    // If it does change, then we need to update the container's height accordingly. Only update the height if the accordion is expanded.
    // Note - This may result in two `setContentHeight` calls when the accordion opens: (1) via the above `useEffect` and (2) in `onResize`
    //        Both `setContentHeight` calls _should_ set the same value, so no unnecessary re-renders would be triggered, making this a harmless additional set call.
    const onResize = (0, react_1.useCallback)(() => {
        if (contentRef.current && expanded) {
            setContentHeight(`${contentRef.current.scrollHeight}px`);
        }
    }, [expanded, setContentHeight]);
    (0, utils_1.useResizeObserver)({ ref: contentRef, onResize });
    const toggle = (0, react_1.useCallback)(() => {
        setExpanded((prev) => !prev);
        if (setExpandedIndex)
            setExpandedIndex(index);
    }, [index, setExpandedIndex]);
    const touchableStyle = (0, react_1.useMemo)(() => ({
        ...Css_1.Css.df.jcsb.gapPx(12).aic.p2.baseMd.outline("none").onHover.bgGray100.if(!!titleOnClick).baseSb.$,
        ...(compact && Css_1.Css.smMd.pl2.prPx(10).py1.bgGray100.mbPx(4).br8.onHover.bgGray200.$),
        ...(compact && !!titleOnClick && Css_1.Css.br0.$),
        ...(disabled && Css_1.Css.gray500.$),
        ...(isFocusVisible && Css_1.Css.boxShadow(`inset 0 0 0 2px ${Css_1.Palette.Blue700}`).$),
        ...xss,
    }), [compact, disabled, isFocusVisible, titleOnClick, xss]);
    return ((0, jsx_runtime_1.jsxs)("div", { ...tid.container, css: {
            ...Css_1.Css.bcGray300.if(topBorder).bt.if(bottomBorder).bb.$,
            ...(size ? Css_1.Css.wPx(accordionSizes[size]).$ : {}),
        }, children: [titleOnClick ? ((0, jsx_runtime_1.jsxs)("div", { ...focusProps, "aria-controls": id, "aria-expanded": expanded, css: Css_1.Css.df.$, children: [(0, jsx_runtime_1.jsx)("button", { ...tid.title, disabled: disabled, css: { ...touchableStyle, ...Css_1.Css.fg1.$ }, onClick: titleOnClick, children: title }), (0, jsx_runtime_1.jsx)("button", { ...tid.toggle, disabled: disabled, css: { ...touchableStyle, ...Css_1.Css.px2.jcfe.if(compact).pxPx(10).$ }, onClick: toggle, children: (0, jsx_runtime_1.jsx)(RotatingChevronIcon, { expanded: expanded }) })] })) : ((0, jsx_runtime_1.jsxs)("button", { ...tid.title, ...focusProps, "aria-controls": id, "aria-expanded": expanded, disabled: disabled, css: { ...Css_1.Css.w100.$, ...touchableStyle }, onClick: toggle, children: [(0, jsx_runtime_1.jsx)("span", { css: Css_1.Css.fg1.tal.$, children: title }), (0, jsx_runtime_1.jsx)(RotatingChevronIcon, { expanded: expanded })] })), (0, jsx_runtime_1.jsx)("div", { ...tid.details, id: id, "aria-hidden": !expanded, css: Css_1.Css.oh.h(contentHeight).add("transition", "height 250ms ease-in-out").$, children: expanded && ((0, jsx_runtime_1.jsx)("div", { css: Css_1.Css.px2.pb2.pt1.if(omitPadding).p0.$, ref: contentRef, ...tid.content, children: children })) })] }));
}
function RotatingChevronIcon(props) {
    return ((0, jsx_runtime_1.jsx)("span", { css: {
            ...Css_1.Css.fs0.$,
            transition: "transform 250ms linear",
            transform: props.expanded ? "rotate(180deg)" : "rotate(0deg)",
        }, children: (0, jsx_runtime_1.jsx)(Icon_1.Icon, { icon: "chevronDown" }) }));
}
const accordionSizes = {
    xs: 240,
    sm: 360,
    md: 480,
    lg: 600,
};
